/**
 * Rotating-captain register — Ben Petty 2026-05-05 (afternoon, note 3).
 *
 * Ben framed this as the highest-leverage social-development move at
 * grassroots: a different player captains the team each session, the
 * coach surfaces "this week's captain: X" in the pre-session brief,
 * and the queue cycles automatically so every player gets a turn.
 *
 * The queue is stored as an ordered jsonb array on `teams.rotating_captain_queue`
 * (migration 00069). HEAD = next player up. TAIL = most recently captained.
 * `sessions.captain_id` records which player actually captained a session
 * — they get rotated to the tail when the session is logged.
 *
 * This module is pure logic — DB reads/writes happen at the call site.
 * That keeps the rotation rules unit-testable and means the same code
 * can run inside an Edge Function, the AI tool layer, or a UI handler.
 */

export interface CaptainQueueEntry {
  /** users.id of the player. */
  playerId: string;
  /** ISO-8601 string of when this player last captained. Null when
   *  they've never captained yet — those players should rotate in
   *  ahead of any player who has captained. */
  lastCaptainedAt: string | null;
}

export type CaptainQueue = readonly CaptainQueueEntry[];

/** Given the current queue, return the player whose turn it is to
 *  captain the next session. Returns null when the queue is empty
 *  (rotation not enabled for the team). */
export function currentCaptain(queue: CaptainQueue): string | null {
  return queue.length > 0 ? queue[0].playerId : null;
}

/** Build a fresh queue from a roster — every player added with
 *  lastCaptainedAt = null. Use this when the coach first enables the
 *  rotating-captain feature for a team. Order is preserved from the
 *  input so the coach can choose the starting order. */
export function initialiseQueue(playerIds: readonly string[]): CaptainQueueEntry[] {
  return playerIds.map((id) => ({ playerId: id, lastCaptainedAt: null }));
}

/**
 * Record that `playerId` captained on `when`. Returns a new queue with that
 * player rotated to the tail and a fresh lastCaptainedAt. When the
 * captain isn't yet in the queue (e.g. a sub stepped up), they're added at
 * the tail.
 *
 * Idempotency: if `when` is the same ISO timestamp as the current entry's
 * lastCaptainedAt, the queue is returned unchanged — calling twice for the
 * same session never double-rotates.
 */
export function recordCaptained(
  queue: CaptainQueue,
  playerId: string,
  when: Date | string,
): CaptainQueueEntry[] {
  const whenIso = typeof when === 'string' ? when : when.toISOString();

  const existing = queue.find((e) => e.playerId === playerId);
  if (existing && existing.lastCaptainedAt === whenIso) {
    // Idempotent — no change.
    return queue.slice();
  }

  const without = queue.filter((e) => e.playerId !== playerId);
  return [...without, { playerId, lastCaptainedAt: whenIso }];
}

/**
 * Add a new player to the queue. New players go to the HEAD by default
 * (so they captain sooner) — a deliberate choice: a new player joining
 * mid-season often benefits from the social welcome of the captaincy
 * early. Pass `position: 'tail'` to defer instead.
 */
export function addPlayerToQueue(
  queue: CaptainQueue,
  playerId: string,
  position: 'head' | 'tail' = 'head',
): CaptainQueueEntry[] {
  if (queue.some((e) => e.playerId === playerId)) {
    // Idempotent — already in the queue.
    return queue.slice();
  }
  const entry: CaptainQueueEntry = { playerId, lastCaptainedAt: null };
  return position === 'head' ? [entry, ...queue] : [...queue, entry];
}

/** Remove a player from the queue (e.g. they've left the team). */
export function removePlayerFromQueue(
  queue: CaptainQueue,
  playerId: string,
): CaptainQueueEntry[] {
  return queue.filter((e) => e.playerId !== playerId);
}

/**
 * Validate a queue value coming from the DB or an API client. Filters
 * out malformed entries (non-string player ids, non-string lastCaptainedAt)
 * and returns the cleaned queue. Returns an empty array for any
 * non-array input so the caller can treat null / undefined / corrupted
 * jsonb as "feature not enabled".
 */
export function parseCaptainQueue(value: unknown): CaptainQueueEntry[] {
  if (!Array.isArray(value)) return [];
  const out: CaptainQueueEntry[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item !== 'object' || item === null) continue;
    const obj = item as Record<string, unknown>;
    const id = obj.playerId ?? obj.player_id;
    if (typeof id !== 'string' || id.length === 0) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    const last = obj.lastCaptainedAt ?? obj.last_captained_at;
    out.push({
      playerId: id,
      lastCaptainedAt: typeof last === 'string' ? last : null,
    });
  }
  return out;
}

/**
 * Render the queue as a plain object array suitable for jsonb storage.
 * The DB column uses snake_case keys for SQL-side legibility; this
 * helper applies that convention while the rest of the app uses
 * camelCase. Mirrors the parseCaptainQueue tolerance for both shapes.
 */
export function serialiseCaptainQueue(
  queue: CaptainQueue,
): { player_id: string; last_captained_at: string | null }[] {
  return queue.map((e) => ({
    player_id: e.playerId,
    last_captained_at: e.lastCaptainedAt,
  }));
}
